import olDragBox from "ol/interaction/DragBox";
import olDraw from "ol/interaction/Draw";
import olSelect from "ol/interaction/Select";
import olWKT from "ol/format/WKT";
import { selectionStyle } from "../../utils/mapStyles";

// import olGeoJSON from 'ol/format/GeoJSON';

// Cache object for current interactions added to map, i
var _interactions = [];

// GeoJSON format function
// const _geoJsonFmt = new olGeoJSON();

// WKT format function
const _wktFmt = new olWKT();

/**
 * Checks if a feature is a cluster feature
 *
 * @param {*} feature Openlayers feature object
 */
export function isCluster(feature) {
  return (
    feature &&
    Array.isArray(feature.get("features")) &&
    feature.get("features").length > 0
  );
}

/**
 * Enable a select interaction
 *
 * @param {*} map Openlayers map object
 * @param {*} drawingLayer Openlayers vector layer object
 * @param {*} setFilter A method that is called to update the selection fitler
 * @param {*} selectionLayer Openlayers vector layer object
 * @param {*} featureProperty Name of property to return from selected features
 * @param {*} filterKey Name of property in filter
 * @param {*} numGeom Max number of elements selectable
 */
export function enableSelectInteraction(
  map,
  drawingLayer,
  setFilter,
  selectionLayer,
  featureProperty = null,
  filterKey = null,
  numGeom = 1
) {
  if (map) {
    if (Array.isArray(_interactions)) {
      _interactions.forEach((ia) => map.removeInteraction(ia));
    }
    _interactions.length = 0;

    if (selectionLayer) {
      selectionLayer.setVisible(true);

      var selectInteraction = new olSelect({
        layers: [selectionLayer],
        multi: numGeom > 1,
        hitTolerance: 10,
        style: selectionStyle,
      });
      _interactions.push(selectInteraction);
      map.addInteraction(selectInteraction);

      var selectedFeatures = selectInteraction.getFeatures();

      // The on add/remove function is called for each feature, debounce
      // to prevent flooding of setState
      var _timeout = null;

      selectedFeatures.on(["add", "remove"], (selEv) => {
        if (_timeout) {
          clearTimeout(_timeout);
        }
        _timeout = setTimeout(() => {
          var updateFilter = {};

          updateFilter["geom"] = selectedFeatures.getArray().map((sft) =>
            _wktFmt.writeFeature(sft, {
              dataProjection: "EPSG:4326",
              featureProjection: "EPSG:3857",
            })
          );

          if (Array.isArray(selectedFeatures.getArray())) {
            selectedFeatures
              .getArray()
              .forEach((feature, featureIdx, featArray) => {
                if (isCluster(feature)) {
                  feature.get("features").forEach((innerFeat) => {
                    featArray.push(innerFeat);
                  });
                  featArray.splice(featureIdx, 1);
                }
              });

            updateFilter[filterKey] = selectedFeatures
              .getArray()
              .map((selectedFeature) => {
                if (featureProperty === "geometry") {
                  return _wktFmt.writeGeometry(selectedFeature.getGeometry(), {
                    dataProjection: "EPSG:4326",
                    featureProjection: "EPSG:3857",
                  });
                } else {
                  return selectedFeature.getProperties()[featureProperty];
                }
              });

            setFilter(updateFilter);
          }
        }, 200);
      });
    }
  }
}

/**
 * Enable a draw interaction
 *
 * @param {*} map Openlayers map object
 * @param {*} drawingLayer Openlayers vector layer object
 * @param {*} setFilter A function to update the filter
 * @param {*} geometryType Geometry type, one of Point, LineString, Polygon
 * @param {*} selectionLayer Openlayers vector layer object
 * @param {*} featureProperty Property to return from selected features
 * @param {*} filterKey Property to assign selection to in filter
 * @param {*} numGeom Max number of selected features
 */
export function enableDrawInteraction(
  map,
  drawingLayer,
  setFilter,
  geometryType,
  selectionLayer = null,
  featureProperty = null,
  filterKey = null,
  numGeom = 1
) {
  if (map) {
    if (Array.isArray(_interactions)) {
      _interactions.forEach((ia) => map.removeInteraction(ia));
    }
    _interactions.length = 0;

    var selectInteraction;
    var selectedFeatures;

    // If select active, add select interaction
    if (selectionLayer) {
      selectionLayer.setVisible(true);
      selectInteraction = new olSelect({
        layers: [selectionLayer],
        style: selectionStyle,
      });
      selectedFeatures = selectInteraction.getFeatures();
      _interactions.push(selectInteraction);
    }

    if (
      ["Point", "LineString", "Polygon", "Circle"].indexOf(geometryType) > -1
    ) {
      // Create and add draw interaction
      var drawInteraction = new olDraw({
        source: drawingLayer.getSource(),
        type: geometryType,
        style: selectionStyle,
      });
      _interactions.push(drawInteraction);

      // Clearing drawing layer when selecting draw polyline button
      // console.debug("Clearing drawingLayer source on click of button");
      // drawingLayer.getSource().clear();

      drawInteraction.on("drawstart", () => {
        if (drawingLayer.getSource().getFeatures().length >= numGeom * 2) {
          // console.debug('Clear on drawstart', drawingLayer.getSource().getFeatures().length, numGeom);
          // drawingLayer.getSource().clear();
          drawingLayer
            .getSource()
            .removeFeature(drawingLayer.getSource().getFeatures().shift());
        }
      });

      drawInteraction.on("drawend", (drawEvent) => {
        var filterUpdate = {};

        var wktGeom = _wktFmt.writeGeometry(drawEvent.feature.getGeometry(), {
          dataProjection: "EPSG:4326",
          featureProjection: "EPSG:3857",
        });

        filterUpdate["geom"] = [wktGeom];

        if (
          geometryType === "Polygon" &&
          selectionLayer &&
          filterKey &&
          featureProperty
        ) {
          var intersectingFeatures = [];

          var drawGeom = drawEvent.feature.getGeometry();
          selectionLayer
            .getSource()
            .forEachFeatureInExtent(drawGeom.getExtent(), (outerFeature) => {
              selectedFeatures.push(outerFeature);

              // If this is a cluster feature
              if (isCluster(outerFeature)) {
                outerFeature.get("features").forEach((innerFeature) => {
                  // Check if polygon intersects extent of each point
                  if (
                    drawGeom.intersectsExtent(
                      innerFeature.getGeometry().getExtent()
                    )
                  ) {
                    intersectingFeatures.push(
                      innerFeature.getProperties()[featureProperty]
                    );
                    selectedFeatures.push(innerFeature);
                  }
                });
                // If this is a regular feature
              } else {
                if (
                  drawGeom.intersectsExtent(
                    outerFeature.getGeometry().getExtent()
                  )
                ) {
                  intersectingFeatures.push(
                    outerFeature.getProperties()[featureProperty]
                  );
                  selectedFeatures.push(outerFeature);
                }
              }
            });

          filterUpdate[filterKey] = intersectingFeatures;
        }

        // In both cases, add as filter
        // drawingLayer.getSource().removeFeature(drawEvent.feature);
        setFilter(filterUpdate);
      });

      if (Array.isArray(_interactions)) {
        _interactions.forEach((i) => {
          map.addInteraction(i);
        });
      }
    }
  }
}

/**
 * Enable a drag box or dag box with selection interaction
 *
 * @param {*} map Openlayers map object
 * @param {*} drawingLayer Openlayers vector layer object
 * @param {*} setFilter A function to update the filter
 * @param {*} selectionLayer Openlayers vector layer object
 * @param {*} featureProperty Property to return from selected features
 * @param {*} filterKey Property to assign selection to in filter
 * @param {*} numGeom Max number of selected features
 */
export function enableDragBoxInteraction(
  map,
  drawingLayer,
  setFilter,
  selectionLayer = null,
  featureProperty = null,
  filterKey = null,
  numGeom = 1
) {
  if (map) {
    if (Array.isArray(_interactions)) {
      _interactions.forEach((ia) => map.removeInteraction(ia));
    }
    _interactions.length = 0;

    var selectInteraction;
    var selectedFeatures;

    // If select active, add select interaction
    if (selectionLayer) {
      selectionLayer.setVisible(true);
      selectInteraction = new olSelect({
        layers: [selectionLayer],
        style: selectionStyle,
      });
      selectedFeatures = selectInteraction.getFeatures();
      _interactions.push(selectInteraction);
    }

    // Create and add dragBox interaction
    var dragBox = new olDragBox({});

    _interactions.push(dragBox);

    dragBox.on("boxstart", () => {
      if (!(drawingLayer.getSource().getFeatures().length < numGeom)) {
        // console.debug('Clearing drawingLayer on boxstart');
        drawingLayer.getSource().clear();
      }
    });

    dragBox.on("boxend", () => {
      var geom = [
        _wktFmt.writeGeometry(dragBox.getGeometry(), {
          dataProjection: "EPSG:4326",
          featureProjection: "EPSG:3857",
        }),
      ];
      var filterUpdate = { geom: geom };

      if (selectionLayer) {
        var intersectingFeatures = [];

        selectionLayer
          .getSource()
          .forEachFeatureInExtent(
            dragBox.getGeometry().getExtent(),
            (feature) => {
              if (isCluster(feature)) {
                feature.get("features").forEach((clusterFeature) => {
                  intersectingFeatures.push(
                    clusterFeature.getProperties()[featureProperty]
                  );
                  selectedFeatures.push(clusterFeature);
                });
              } else {
                intersectingFeatures.push(
                  feature.getProperties()[featureProperty]
                );
                selectedFeatures.push(feature);
              }
            }
          );
        filterUpdate[filterKey] = intersectingFeatures;
      }
      setFilter(filterUpdate);
    });

    if (Array.isArray(_interactions)) {
      _interactions.forEach((i) => {
        map.addInteraction(i);
      });
    }
  }
}
